import { HttpErrorResponse } from '@angular/common/http';
import {
	ChangeDetectorRef,
	Component,
	Input,
	OnInit,
	ViewChild,
	EventEmitter,
	Output,
	ElementRef,
	HostListener
} from '@angular/core';
import { Router } from '@angular/router';
import { untilComponentDestroyed, OnDestroyMixin } from '@w11k/ngx-componentdestroyed';
import { PriceDto } from '../../api';
import { NarwhalDto, NarwhalListResultDto, MaxNarwhalProperties } from '../../api/narwhalListResultDto'
import { NarwhalNameUpdateReq } from '../../api/narwhalNameUpdateReq'
import { AuthService } from '../auth/auth.service';
import { NarwhalService } from '../../narwhal.service';
import { PriceService } from '../../price.service';
import { LoginModalService } from '../login/login-modal.service';
import { BowserService } from 'ngx-bowser';
import { defs, DigitsInfo } from '../../defs';
import { Subscription } from 'rxjs';
import { InventoryItemDto } from '../../api/inventoryItemDto';
import { NarwhalQueryParameters } from '../../api/narwhal.query';

@Component({
	selector: 'narwhalia-narwhal-card',
	templateUrl: './narwhal-card.component.html',
	styleUrls: ['./narwhal-card.component.scss'],
	providers: [PriceService]
})
export class NarwhalCardComponent extends OnDestroyMixin implements OnInit {
	@Input() narwhal: NarwhalDto;
	@Input() maxProps: MaxNarwhalProperties;
	@Input() type = 'population';
	@ViewChild('input') input;
	@Output() nameUpdateEmitter: EventEmitter<NarwhalNameUpdateReq>;
	digitsInfo: DigitsInfo;
	showBuyPopup: boolean;
	ownId: string;
	showOfferPopup: boolean;
	isNameUnderEdit: boolean;
	oldName: string;
	showApplyPopup: boolean;
	isLoggedIn: boolean;
	inventorySum: number;
	isEdge: boolean;
	priceSubs: Subscription

	@HostListener('document:click', ['$event'])
	clickedOutside(event) {
		if (!this.isEdge) {
			const path = event.path || (event.composedPath && event.composedPath());
			const sourceCard = path.find(element => element.localName === 'narwhalia-narwhal-card');
			const isApplyButtonClicked = path.some(element => element.className && element.className.includes('nar-icon--apply'));
			if (sourceCard && sourceCard.id !== this.elementRef.nativeElement.id && isApplyButtonClicked) {
				this.showApplyPopup = false;
			} else if (isApplyButtonClicked) {
				this.showApplyPopup = true;
			}
		}
	}

	constructor(
		private narwhalService: NarwhalService,
		private authService: AuthService,
		private loginModalService: LoginModalService,
		private priceService: PriceService,
		private cdRef: ChangeDetectorRef,
		private router: Router,
		private elementRef: ElementRef,
		private bowserService: BowserService
	) {
		super();
		this.digitsInfo = defs.digitsInfo;
		this.nameUpdateEmitter = new EventEmitter<NarwhalNameUpdateReq>();
	}

	ngOnInit() {
		if (this.bowserService.bowser.chrome || this.bowserService.bowser.firefox) {
			this.isEdge = false;
		} else {
			this.isEdge = true;
		}

		this.authService
			.isLoggedInAsObservable()
			.pipe(untilComponentDestroyed(this))
			.subscribe(isLoggedIn => {
				this.isLoggedIn = isLoggedIn;
				this.ownId = this.authService.getCurrentUserId();
				if (this.isLoggedIn && this.ownId === this.narwhal.owner.id) {
					this.narwhal.canLike = false;
				}
			});

		if (this.narwhal.price) {
			this.subscribePriceChange();
		}

		this.narwhalService.inventoryItems$.subscribe(inventoryItems => {
			this.inventorySum = inventoryItems.reduce((acc, curr) => acc + curr.count, 0);
		});

		const maxProductivity = this.narwhal.gender === 'male' ? this.maxProps.glitter : this.maxProps.birth;

		this.narwhal = {
			...this.narwhal,
			fitnessPercentage: ((this.narwhal.fitness / this.maxProps.fitness) * 100).toFixed(1),
			productivityPercentage: ((this.narwhal.productivity / maxProductivity) * 100).toFixed(1)
		};
	}

	closeApplyPopup() {
		this.showApplyPopup = false;
	}

	toggleBuyPopup(event) {
		event.stopPropagation();
		if (this.authService.isLoggedIn()) {
			this.showBuyPopup = !this.showBuyPopup;
		} else {
			this.loginModalService.open();
		}
	}

	toggleOfferPopup(event) {
		event.stopPropagation();
		this.showOfferPopup = !this.showOfferPopup;
	}

	toggleApplyPopup(event) {
		if (this.isEdge) {
			event.stopPropagation();
			this.showApplyPopup = !this.showApplyPopup;
		}
	}

	closeBuyPopup(isBuySuccessful) {
		if (isBuySuccessful) {
			this.narwhal.lockBuy = true;
		}
		this.showBuyPopup = false;
	}

	closeOfferPopup() {
		this.showOfferPopup = false;
	}

	onSale(): boolean {
		return this.narwhal.price && this.narwhal.price.act > 0
	}

	canBuy(): boolean {
		return this.onSale() || this.narwhal.lockBuy;
	}

	subscribePriceChange() {
		this.priceSubs = this.priceService.getActualPrice(this.narwhal.price, this).subscribe();
	}

	startSell(price: PriceDto) {
		this.narwhal.price = price;
		this.subscribePriceChange();
	}

	stopSell(narwhal: NarwhalDto) {
		this.narwhalService.stopSell(narwhal.id)
			.pipe(untilComponentDestroyed(this))
			.subscribe(
				() => {this.narwhal.price = undefined;
						this.priceSubs.unsubscribe()
				},
				err => {
					if (err instanceof HttpErrorResponse && err.status === 409) {
						this.narwhal = { ...this.narwhal, lockBuy: true };
					}
				}
			);
	}

	like() {
		if (this.type === 'population') {
			if (this.authService.isLoggedIn()) {
				this.narwhalService.like(this.narwhal.id)
					.pipe(untilComponentDestroyed(this))
					.subscribe(() => {
						this.narwhal.canLike = false;
						this.narwhal.likes += 1;
					});
			} else {
				this.loginModalService.open();
			}
		}
	}

	onNameFocus() {
		this.isNameUnderEdit = true;
		this.oldName = this.narwhal.name;
		this.cdRef.detectChanges();
		this.input.nativeElement.focus();
	}

	onNameEscape(event) {
		if (event && event.relatedTarget && (event.relatedTarget.name === 'ok' || event.relatedTarget.name === 'cancel')) {
			return;
		}
		this.narwhal.name = this.oldName;
		this.isNameUnderEdit = false;
	}

	saveName() {
		this.narwhal.name = this.narwhal.name.trim();
		if (this.oldName !== this.narwhal.name && this.narwhal.name !== '') {
			this.updateNarwhalName(this.narwhal.id, this.narwhal.name);
		} else {
			this.narwhal.name = this.oldName;
			this.isNameUnderEdit = false;
		}
	}

	updateNarwhalName(narwhalId: string, name: string) {
		this.narwhalService.rename(narwhalId, name).subscribe(result => {
			this.isNameUnderEdit = false;
			const nameUpdate: NarwhalNameUpdateReq = {name, narwhalId };
			this.nameUpdateEmitter.emit(nameUpdate);
			this.oldName = nameUpdate.name;
		});
	}

	filterByClanName() {
		const actualFilter = this.type === 'population' ? defs.populationFilter : defs.mySwarmFilter;
		const parsedQueryParams = JSON.parse(localStorage.getItem(actualFilter));
		const { ownerId, ...queryParams } = parsedQueryParams;
		this.router.navigate([this.type], { queryParams: { ...queryParams, clanName: this.narwhal.clanName } });
	}

	filterByOwnerName() {
		const queryParams = JSON.parse(localStorage.getItem(defs.populationFilter));
		this.router.navigate(['population'], { queryParams: { ...queryParams, ownerName: this.narwhal.owner.name } });
	}

	refreshNarwhal(inventoryItems: InventoryItemDto[]) {
		inventoryItems.forEach(inventoryItem => {
			inventoryItem.improvements.forEach(improvement => {
				if (this.narwhal[`${improvement.name.toLowerCase()}Level`] + improvement.level <= 5) {
					this.narwhal[improvement.name.toLowerCase()] = this.narwhal[improvement.name.toLowerCase()] * (1 + improvement.value / 100);
					this.narwhal[`${improvement.name.toLowerCase()}Level`] =
						this.narwhal[`${improvement.name.toLowerCase()}Level`] + improvement.level;
				}
				let queryParams: NarwhalQueryParameters = JSON.parse(localStorage.getItem('swarmQueryParams'));
				queryParams = { ...queryParams, ps: 1, p: 1 };
				this.narwhalService.getNarwhals(queryParams).subscribe((result: NarwhalListResultDto) => {
					const maxProductivity = this.narwhal.gender === 'male' ? result.max.glitter : result.max.birth;
					(this.narwhal.fitnessPercentage = ((this.narwhal.fitness / result.max.fitness) * 100).toFixed(1)),
						(this.narwhal.productivityPercentage = ((this.narwhal.productivity / maxProductivity) * 100).toFixed(1));
				});
			});
		});
	}
}